import { TransportError } from 'ProdantixSdk/core/errors';
import { type FetchLike, makeAbortController, resolveFetch, trimTrailingSlash } from 'ProdantixSdk/core/http';
import type { EventProperties } from 'ProdantixSdk/core/types';

export const CTA_VIEWED = '$cta_viewed';
export const CTA_CLICKED = '$cta_clicked';
export const CTA_DISMISSED = '$cta_dismissed';

export type CtaKind = 'banner' | 'embedded' | 'popup' | 'slide_in';

export type CtaPlacement = 'bottom' | 'bottom_left' | 'bottom_right' | 'top';

export type CtaButtonAction = { kind: 'dismiss' } | { kind: 'form'; value: string } | { kind: 'messenger' } | { kind: 'url'; value: string };

export interface CtaButton {
  action: CtaButtonAction;
  label: string;
}

export interface CtaContent {
  body: string;
  buttons: CtaButton[];
  heading: string;
  image: string;
  imageAlt: string;
}

export interface CtaStyle {
  background: string;
  button: string;
  buttonText: string;
  radius: number;
  text: string;
}

export type CtaTrigger = { kind: 'click'; value: string } | { kind: 'delay'; value: number } | { kind: 'exit' } | { kind: 'load' } | { kind: 'scroll'; value: number };

export type CtaFrequency = { kind: 'days'; value: number } | { kind: 'once' } | { kind: 'session' };

export interface ServedCta {
  flag: { key: string; variant?: string | null } | null;
  frequency: CtaFrequency;
  goalEvent: string | null;
  id: string;
  kind: CtaKind;
  placement: CtaPlacement;
  /** Whether the CTA carries the "Powered by Prodantix" line. */
  poweredBy: boolean;
  trigger: CtaTrigger;
  /** The one variant this visitor is shown. */
  variant: { content: CtaContent; key: string; style: CtaStyle };
  version: number;
}

export type CtaDevice = 'desktop' | 'mobile' | 'tablet';

export interface CtaPageView {
  anonymous: boolean;
  device: CtaDevice;
  distinctId: string;
  language: string;
  url: string;
}

export interface CtasClientOptions {
  fetch?: FetchLike;
  host: string;
  projectKey: string;
  requestTimeoutMs?: number;
}

/** The properties every CTA event carries, and the button for a click. */
export const ctaProperties = (cta: ServedCta, button?: number): EventProperties => {
  const properties: EventProperties = { $cta_id: cta.id, $cta_kind: cta.kind, $cta_variant: cta.variant.key, $cta_version: cta.version };
  const pressed = button === undefined ? undefined : cta.variant.content.buttons[button];
  if (pressed && button !== undefined) {
    properties.$cta_button = button;
    properties.$cta_button_label = pressed.label;
    if (pressed.action.kind === 'form') {
      properties.$cta_form_id = pressed.action.value;
    }
  }
  return properties;
};

export class CtasClient {
  private readonly fetch?: FetchLike;
  private readonly host: string;
  private readonly projectKey: string;
  private readonly requestTimeoutMs: number;

  constructor(options: CtasClientOptions) {
    this.fetch = resolveFetch(options.fetch);
    this.host = trimTrailingSlash(options.host);
    this.projectKey = options.projectKey;
    this.requestTimeoutMs = options.requestTimeoutMs ?? 10_000;
  }

  /** The live CTAs a page view may show this visitor. */
  async ctas(view: CtaPageView): Promise<ServedCta[]> {
    const fetchImpl = this.fetch;
    if (!fetchImpl) {
      throw new TransportError('no fetch implementation available in this runtime');
    }
    const query = new URLSearchParams({ anonymous: String(view.anonymous), device: view.device, distinct_id: view.distinctId, language: view.language, url: view.url });
    const controller = makeAbortController();
    const timer = controller ? setTimeout(() => controller.abort(), this.requestTimeoutMs) : undefined;
    try {
      const response = await fetchImpl(`${this.host}/v1/ctas?${query.toString()}`, { headers: { authorization: `Bearer ${this.projectKey}` }, method: 'GET', signal: controller?.signal });
      if (response.status < 200 || response.status >= 300) {
        throw new TransportError(`ctas request failed with status ${response.status}`, response.status);
      }
      return (JSON.parse(await response.text()) as { ctas: ServedCta[] }).ctas;
    } finally {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    }
  }
}
